import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Spinner } from 'react-bootstrap';
import { CForm, CFormInput, CFormSelect } from '@coreui/react';
import { ogaRegisterSchema } from '../../../components/utilities/models/gen-models';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../../../components/utilities/axios';
import LandingHeader from '../landing-page/landing-header/LandingHeader';
import { NIGERIA_STATES } from './nigeria-states';
import './registration.css';

const MINIMUM_TOP_UP = 10000;

const REGISTRATION_STEPS = [
  { title: 'Company information', shortTitle: 'Company' },
  { title: 'Additional information', shortTitle: 'Details' },
  { title: 'Login information', shortTitle: 'Login' },
  { title: 'Fund your e-Wallet', shortTitle: 'Funding' },
];

const STEP_FIELDS = [
  [
    'companyName',
    'rcNumber',
    'referralAgentCode',
    'state',
    'lga',
    'cityName',
    'streetNumber',
    'latitude',
    'longitude',
  ],
  [
    'phoneNumber',
    'emailAddress',
    'firstName',
    'lastName',
    'contactPhoneNumber',
    'vitelPhoneNumber',
  ],
  ['userName', 'password', 'confirmPassword'],
  ['amount', 'termsConditions'],
];

const SERVICE_DETAILS = [
  {
    title: 'CUG/Xphone (with vSIM) and Company Phone Lines',
    description:
      'A Closed User Group (CUG) lets company lines call each other free or at reduced rates. Vitel CUG uses Xphone for unlimited calls among employees and other Xphone users worldwide. Calls to other networks are at a very reduced rate. All OgaApp phone lines are enrolled in Xphone.',
  },
  {
    title: 'Employee Communication Benefits (ECB)',
    description:
      'All company phone lines on OgaApp are enrolled in the ECB package and can receive monthly airtime and data top-ups. Customers can set a different amount for each line.',
  },
  {
    title: 'Auto-Attendant',
    description:
      'Auto-Attendant is a dedicated phone line that answers calls professionally, provides recorded instructions, and routes callers to any phone number worldwide. Customers can subscribe to as many lines as needed.',
  },
  {
    title: 'OgaBoiz',
    description:
      'OgaBoiz is a time and attendance system that tracks employees’ locations during work hours. It automatically clocks staff in and out based on their location and includes time-off and payroll features. Customers can subscribe to OgaBoiz and add the company lines they want to manage.',
  },
  {
    title: 'SecureDevice',
    description:
      'SecureDevice uses Vitel Wireless location-awareness technology to track, locate, and recover devices equipped with a Vitel eSIM or SIM card. Customers can add vehicle trackers, 4G cameras, body-worn cameras, smartwatches, and other compatible devices. A command-and-control dashboard displays each asset’s location and lets customers manage its airtime and data bundles.',
  },
  {
    title: 'SecureMe',
    description:
      'SecureMe is a family safety service for employees. The USSD and mobile app use Vitel Wireless technology to locate family and friends during emergencies and display their location on a map when SOS is triggered. Customers can assign the service to selected company lines.',
  },
];

const formatNigerianPhone = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('234') && digits.length > 10) return `+${digits}`;
  return `+234${digits.replace(/^0/, '')}`;
};

const FieldLabel = ({ htmlFor, children, required, optional }) => (
  <label className="registration-label" htmlFor={htmlFor}>
    <span>
      {children}
      {required && <span className="registration-required"> *</span>}
      {optional && <span className="registration-optional">Optional</span>}
    </span>
  </label>
);

const FieldError = ({ children }) =>
  children ? <div className="registration-error">{children}</div> : null;

const PhoneField = ({ id, label, registerProps, error, optional = false }) => (
  <div className="registration-field">
    <FieldLabel htmlFor={id} required={!optional} optional={optional}>
      {label}
    </FieldLabel>
    <div className="registration-phone-input">
      <span className="registration-phone-prefix">+234</span>
      <CFormInput
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="801 234 5678"
        {...registerProps}
      />
    </div>
    <FieldError>{error}</FieldError>
  </div>
);

const OGAAppRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [showError, setShowError] = useState(false);
  const [registered, setRegistered] = useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(null);
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [referralStatus, setReferralStatus] = useState({
    checking: false,
    valid: false,
    checked: false,
    message: '',
  });

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(ogaRegisterSchema),
    mode: 'onBlur',
    defaultValues: {
      amount: MINIMUM_TOP_UP,
      termsConditions: false,
    },
  });

  const referralCodeValue = watch('referralAgentCode') || '';
  const selectedState = watch('state') || '';
  const selectedLga = watch('lga') || '';
  const selectedAmount = Number(watch('amount') || 0);
  const latitude = watch('latitude');
  const longitude = watch('longitude');

  useEffect(() => {
    let mounted = true;
    const fetchStates = async () => {
      try {
        const { data } = await axios.get('/generals/states');
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.states)
              ? data.states
              : [];
        if (mounted) {
          setStates(
            list.length
              ? list
              : NIGERIA_STATES.map((name) => ({ stateId: name, name })),
          );
        }
      } catch (error) {
        if (mounted) {
          setStates(NIGERIA_STATES.map((name) => ({ stateId: name, name })));
        }
      }
    };
    fetchStates();
    return () => {
      mounted = false;
    };
  }, []);

  const handleStateChange = async (event) => {
    const stateValue = event.target.value;
    setValue('state', stateValue, { shouldValidate: true });
    setValue('lga', '', { shouldValidate: true });
    setLgas([]);
    if (!stateValue) return;

    setLoadingLgas(true);
    try {
      const { data } = await axios.get('/generals/local_govt', {
        params: { stateId: stateValue },
      });
      const values = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.localGovt)
            ? data.localGovt
            : Array.isArray(data?.lgas)
              ? data.lgas
              : [];
      const selectedStateData = states.find(
        (state) =>
          String(state.stateId) === String(stateValue) ||
          String(state.name) === String(stateValue),
      );
      const selectedStateId = selectedStateData?.stateId || stateValue;
      const stateValues = values.filter(
        (item) => String(item?.stateId) === String(selectedStateId),
      );

      setLgas(
        stateValues
          .map((item) => ({
            lgaId: item?.lgaId ?? item?.localGovtId ?? item?.id ?? item?.name,
            name: item?.name ?? item?.lga ?? item?.localGovtName ?? String(item),
          }))
          .filter((item) => item.name && String(item.name).trim()),
      );
    } catch (error) {
      setLgas([]);
    } finally {
      setLoadingLgas(false);
    }
  };

  const checkReferralCode = async (code) => {
    if (!code || !code.trim()) {
      setReferralStatus({
        checking: false,
        valid: false,
        checked: false,
        message: '',
      });
      return;
    }
    setReferralStatus((status) => ({
      ...status,
      checking: true,
      checked: false,
      message: '',
    }));
    try {
      const { data } = await axios.post('/registrations/check-referral', {
        referralAgentCode: code,
      });
      setReferralStatus({
        checking: false,
        valid: Boolean(data?.valid),
        checked: true,
        message:
          data?.message ||
          (data?.valid
            ? data?.agent?.fullName
              ? `Verified — ${data.agent.fullName}`
              : 'Referral code verified'
            : 'Invalid referral code'),
      });
    } catch (error) {
      setReferralStatus({
        checking: false,
        valid: false,
        checked: true,
        message: 'Unable to verify referral code',
      });
    }
  };

  const fetchLocation = () => {
    setShowError(false);
    setRegisterError('');
    if (!navigator.geolocation) {
      setShowError(true);
      setRegisterError('Geolocation is not supported by your browser');
      return;
    }
    setRegisterError('Getting your current location…');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLatitude = String(position.coords.latitude);
        const nextLongitude = String(position.coords.longitude);
        setValue('latitude', nextLatitude, { shouldValidate: true });
        setValue('longitude', nextLongitude, { shouldValidate: true });
        setRegisterError('');
        setShowError(false);
      },
      (error) => {
        setShowError(true);
        setRegisterError(`Unable to get location: ${error.message}`);
      },
    );
  };

  const handleRegister = async (details) => {
    setShowError(false);
    setRegisterError('');
    const payload = { ...details };
    // delete payload.confirmPassword;
    delete payload.termsConditions;
    payload.amount = Number(payload.amount);
    payload.phoneNumber = formatNigerianPhone(payload.phoneNumber);
    payload.contactPhoneNumber = formatNigerianPhone(
      payload.contactPhoneNumber,
    );
    payload.vitelPhoneNumber = formatNigerianPhone(payload.vitelPhoneNumber);

    try {
      const { data } = await axios.post('/registrations/register-init', payload);
      if (data?.success && data?.data?.authorization_url) {
        window.location.href = data.data.authorization_url;
        return;
      }
      setShowError(true);
      setRegisterError(
        data?.message || 'Payment could not be started. Please try again.',
      );
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Network error. Please try again.';
      setShowError(true);
      setRegisterError(message);
    }
  };

  const verifyRegistration = useCallback(
    async (reference) => {
      try {
        const { data } = await axios.get(
          `/registrations/register-verify?reference=${reference}`,
        );
        if (data?.success && data?.userDetails) {
          setRegistered(data);
          reset();
        } else if (data?.message?.toLowerCase().includes('activate')) {
          setPaymentConfirmed({
            reference: data?.data?.reference || reference,
          });
        } else {
          setShowError(true);
          setRegisterError(
            data?.message || 'Payment verification failed. Please try again.',
          );
        }
      } catch (error) {
        setShowError(true);
        setRegisterError('We could not verify your payment right now.');
      }
    },
    [reset],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reference = params.get('reference') || params.get('trxref');
    if (reference) {
      verifyRegistration(reference);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [location.search, verifyRegistration]);

  const goToNextStep = async () => {
    const valid = await trigger(STEP_FIELDS[currentStep]);
    if (valid) {
      setCurrentStep((step) => Math.min(step + 1, REGISTRATION_STEPS.length - 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInvalid = (validationErrors) => {
    const firstInvalidField = STEP_FIELDS.flat().find(
      (field) => validationErrors[field],
    );
    if (!firstInvalidField) return;
    const invalidStep = STEP_FIELDS.findIndex((fields) =>
      fields.includes(firstInvalidField),
    );
    if (invalidStep >= 0) setCurrentStep(invalidStep);
  };

  const selectAmount = (amount) => {
    setValue('amount', amount, { shouldValidate: true, shouldDirty: true });
  };

  if (paymentConfirmed) {
    return (
      <div className="registration-page registration-status-page">
        <LandingHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        <main className="registration-shell registration-status-shell">
          <section className="registration-status-card">
            <div className="registration-status-icon">✓</div>
            <p className="registration-eyebrow">Payment received</p>
            <h1>Payment confirmed. We will activate your account shortly.</h1>
            <p>
              Your registration is complete and your account is being prepared
              for you. No further action is needed.
            </p>
            <div className="registration-status-note">
              <strong>What happens next</strong>
              <ol>
                <li>Your payment has been verified.</li>
                <li>Your OgaApp profile and partner account are being created.</li>
                <li>We will email your Oga Code and login details.</li>
              </ol>
            </div>
            {paymentConfirmed.reference && (
              <p className="registration-reference">
                Reference: <span>{paymentConfirmed.reference}</span>
              </p>
            )}
            <button
              type="button"
              className="registration-primary-button"
              onClick={() => navigate('/oga-app')}
            >
              Proceed to Sign in
            </button>
            <button
              type="button"
              className="registration-secondary-button registration-status-back"
              onClick={() => setPaymentConfirmed(null)}
            >
              Back
            </button>
          </section>
        </main>
      </div>
    );
  }

  if (registered) {
    const userDetails = registered.userDetails || {};
    return (
      <div className="registration-page registration-success-page">
        <LandingHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        <main className="registration-success-shell">
          <section className="registration-success-card">
            <div className="registration-success-heading">
              <div>
                <p className="registration-eyebrow">Welcome to OgaApp</p>
                <h1>Your account is ready</h1>
              </div>
              <button
                type="button"
                className="registration-print-button"
                onClick={() => window.print()}
              >
                Print copy
              </button>
            </div>

            <div className="registration-success-account">
              {userDetails.ogaCode && (
                <div>
                  <span>Your Oga Code</span>
                  <strong>{userDetails.ogaCode}</strong>
                </div>
              )}
              {userDetails.emailAddress && (
                <div>
                  <span>Account email</span>
                  <strong>{userDetails.emailAddress}</strong>
                </div>
              )}
            </div>

            <p className="registration-success-intro">
              Your OgaApp account is now active. You can instantly get phone
              numbers, make calls with Xphone, and add services as needed. Some
              services require a KYC profile (Vitel Wireless Number) and, when
              applicable, additional company documents.
            </p>

            <section className="registration-get-started">
              <h2>Get started</h2>
              <p>
                After signing in, go to Admin → Add Services → Company Phones to
                add phone numbers to your account.
              </p>
            </section>

            <section className="registration-services">
              <h2>Services available</h2>
              <h3>OgaApp Features: Core Functions and Optional Services</h3>
              <p>
                OgaApp is a comprehensive business management application with
                optional features customers can add to their accounts. Most
                tools help business owners and managers oversee operations,
                manage employees, and automate routine tasks. Others provide
                employee benefits that support recognition, motivation, and
                retention.
              </p>
              <div className="registration-service-list">
                {SERVICE_DETAILS.map((service) => (
                  <article className="registration-service" key={service.title}>
                    <h4>{service.title}</h4>
                    <p>{service.description}</p>
                  </article>
                ))}
              </div>
            </section>

            <div className="registration-success-actions">
              <h2>Login into OgaApp now</h2>
              <button
                type="button"
                className="registration-primary-button"
                onClick={() => navigate('/oga-app')}
              >
                Login
              </button>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="registration-page">
      <LandingHeader menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
      <main className="registration-shell">
        <header className="registration-hero">
          <p className="registration-eyebrow">OgaApp Enterprise</p>
          <h1>OgaApp Enterprise Account</h1>
          <p>
            To open an OgaApp account, fill in the form and top-up your account
            with a minimum of N10,000.
          </p>
          {/*<div className="registration-minimum-badge">*/}
          {/*  Minimum e-Wallet funding: N{MINIMUM_TOP_UP.toLocaleString()}*/}
          {/*</div>*/}
        </header>

        <nav className="registration-progress" aria-label="Registration progress">
          {REGISTRATION_STEPS.map((step, index) => (
            <button
              type="button"
              key={step.shortTitle}
              className={`registration-progress-step ${index === currentStep ? 'is-current' : ''} ${index < currentStep ? 'is-complete' : ''}`}
              onClick={() => {
                if (index < currentStep) setCurrentStep(index);
              }}
              disabled={index > currentStep}
              aria-current={index === currentStep ? 'step' : undefined}
            >
              <span className="registration-progress-number">
                {index < currentStep ? '✓' : index + 1}
              </span>
              <span>
                <strong>{step.shortTitle}</strong>
                <small>{step.title}</small>
              </span>
            </button>
          ))}
        </nav>

        <div className="registration-layout">
          <section className="registration-form-card">
            <div className="registration-form-header">
              <div>
                <p className="registration-step-label">
                  Step {currentStep + 1} of {REGISTRATION_STEPS.length}
                </p>
                <h2>{REGISTRATION_STEPS[currentStep].title}</h2>
              </div>
              <span className="registration-secure-label">Secure registration</span>
            </div>

            <CForm onSubmit={handleSubmit(handleRegister, handleInvalid)}>
              {currentStep === 0 && (
                <div className="registration-step-content">
                  <div className="registration-form-grid">
                    <div className="registration-field registration-field-full">
                      <FieldLabel htmlFor="companyName" required>
                        Company Name
                      </FieldLabel>
                      <CFormInput
                        id="companyName"
                        type="text"
                        placeholder="If not a company, enter your full name"
                        {...register('companyName')}
                      />
                      <span className="registration-hint">
                        If not a company, enter your full name.
                      </span>
                      <FieldError>{errors.companyName?.message}</FieldError>
                    </div>
                    <div className="registration-field">
                      <FieldLabel htmlFor="rcNumber" optional>
                        RC#
                      </FieldLabel>
                      <CFormInput
                        id="rcNumber"
                        type="text"
                        placeholder="e.g. RC1234567"
                        {...register('rcNumber')}
                      />
                      <span className="registration-hint">
                        If your company is registered with CAC.
                      </span>
                      <FieldError>{errors.rcNumber?.message}</FieldError>
                    </div>
                    <div className="registration-field">
                      <FieldLabel htmlFor="referralAgentCode" optional>
                        Agent Code
                      </FieldLabel>
                      <div className="registration-inline-input">
                        <CFormInput
                          id="referralAgentCode"
                          type="text"
                          placeholder="Enter agent code"
                          {...register('referralAgentCode')}
                        />
                        <button
                          type="button"
                          className="registration-check-button"
                          onClick={() => checkReferralCode(referralCodeValue)}
                          disabled={referralStatus.checking}
                        >
                          {referralStatus.checking ? (
                            <Spinner size="sm" />
                          ) : (
                            'Check'
                          )}
                        </button>
                      </div>
                      {referralStatus.checked && (
                        <div
                          className={`registration-referral-status ${referralStatus.valid ? 'is-valid' : 'is-invalid'}`}
                        >
                          {referralStatus.message}
                        </div>
                      )}
                      <FieldError>{errors.referralAgentCode?.message}</FieldError>
                    </div>
                  </div>

                  <div className="registration-section-divider" />
                  <h3 className="registration-subsection-title">Your office address</h3>
                  <div className="registration-form-grid">
                    <div className="registration-field">
                      <FieldLabel htmlFor="state" required>
                        State
                      </FieldLabel>
                      <CFormSelect
                        id="state"
                        value={selectedState}
                        {...register('state')}
                        onChange={handleStateChange}
                      >
                        <option value="">Select State</option>
                        {(states.length
                          ? states
                          : NIGERIA_STATES.map((name) => ({ stateId: name, name }))
                        ).map((state) => {
                          const stateId =
                            state.stateId ?? state.id ?? state.stateName ?? state.name;
                          const stateName = state.name ?? state.stateName ?? state.state;
                          return (
                            <option key={stateId} value={stateId}>
                              {stateName}
                            </option>
                          );
                        })}
                      </CFormSelect>
                      <FieldError>{errors.state?.message}</FieldError>
                    </div>
                    <div className="registration-field">
                      <FieldLabel htmlFor="lga" required>
                        LGA
                      </FieldLabel>
                      {loadingLgas ? (
                        <div className="registration-loading">
                          <Spinner size="sm" /> Loading LGAs…
                        </div>
                      ) : lgas.length ? (
                         <CFormSelect
                           id="lga"
                           value={selectedLga}
                           {...register('lga')}
                           onChange={(event) =>
                             setValue('lga', event.target.value, {
                               shouldValidate: true,
                               shouldDirty: true,
                             })
                           }
                         >

                          <option value="">Select LGA</option>
                          {lgas.map((lga) => (
                            <option key={lga.lgaId} value={lga.name}>
                              {lga.name}
                            </option>
                          ))}
                        </CFormSelect>
                      ) : (
                         <CFormInput
                           id="lga"
                           type="text"
                           placeholder="Select or enter LGA"
                           {...register('lga')}
                           onChange={(event) =>
                             setValue('lga', event.target.value, {
                               shouldValidate: true,
                               shouldDirty: true,
                             })
                           }
                         />

                      )}
                      <FieldError>{errors.lga?.message}</FieldError>
                    </div>
                    <div className="registration-field">
                      <FieldLabel htmlFor="cityName" required>
                        Town / area
                      </FieldLabel>
                      <CFormInput
                        id="cityName"
                        type="text"
                        placeholder="Enter town or area"
                        {...register('cityName')}
                      />
                      <FieldError>{errors.cityName?.message}</FieldError>
                    </div>
                    <div className="registration-field">
                      <FieldLabel htmlFor="streetNumber" required>
                        Street number &amp; name
                      </FieldLabel>
                      <CFormInput
                        id="streetNumber"
                        type="text"
                        placeholder="e.g. 12, Ogunlana Drive"
                        {...register('streetNumber')}
                      />
                      <FieldError>{errors.streetNumber?.message}</FieldError>
                    </div>
                  </div>

                  <div className="registration-gps-box">
                    <div className="registration-gps-heading">
                      <div>
                        <strong>GPS coordinates</strong>
                        <p>
                          Optional. Coordinates help verify your address and deliver
                          services. If you are at your business address, click Auto
                          Fill; otherwise, enter coordinates from Google Maps.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="registration-autofill-button"
                        onClick={fetchLocation}
                      >
                        Auto Fill
                      </button>
                    </div>
                    <div className="registration-form-grid">
                      <div className="registration-field">
                        <FieldLabel htmlFor="latitude">Lat</FieldLabel>
                        <CFormInput
                          id="latitude"
                          type="number"
                          step="any"
                          placeholder="e.g. 6.5244"
                          {...register('latitude')}
                        />
                        <FieldError>{errors.latitude?.message}</FieldError>
                      </div>
                      <div className="registration-field">
                        <FieldLabel htmlFor="longitude">Long</FieldLabel>
                        <CFormInput
                          id="longitude"
                          type="number"
                          step="any"
                          placeholder="e.g. 3.3792"
                          {...register('longitude')}
                        />
                        <FieldError>{errors.longitude?.message}</FieldError>
                      </div>
                    </div>
                    {latitude && longitude && (
                      <div className="registration-coordinate-status">
                        Coordinates set: {latitude}, {longitude}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {currentStep === 1 && (
                <div className="registration-step-content">
                  <div className="registration-info-box">
                    <strong>Additional company information</strong>
                    <span>
                      Use your business contact details where available. If you do
                      not have a company line or email, use your personal details.
                    </span>
                  </div>
                  <div className="registration-form-grid">
                    <PhoneField
                      id="phoneNumber"
                      label="Company Phone#"
                      registerProps={register('phoneNumber')}
                      error={errors.phoneNumber?.message}
                    />
                    <div className="registration-field">
                      <FieldLabel htmlFor="emailAddress" required>
                        Company Email address
                      </FieldLabel>
                      <CFormInput
                        id="emailAddress"
                        type="email"
                        placeholder="company@example.com"
                        {...register('emailAddress')}
                      />
                      <span className="registration-hint">
                        If none, use your personal email.
                      </span>
                      <FieldError>{errors.emailAddress?.message}</FieldError>
                    </div>
                  </div>

                  <div className="registration-section-divider" />
                  <h3 className="registration-subsection-title">
                    Primary contact person
                  </h3>
                  <div className="registration-form-grid">
                    <div className="registration-field">
                      <FieldLabel htmlFor="firstName" required>
                        First name
                      </FieldLabel>
                      <CFormInput
                        id="firstName"
                        type="text"
                        placeholder="First name"
                        {...register('firstName')}
                      />
                      <FieldError>{errors.firstName?.message}</FieldError>
                    </div>
                    <div className="registration-field">
                      <FieldLabel htmlFor="lastName" required>
                        Last name
                      </FieldLabel>
                      <CFormInput
                        id="lastName"
                        type="text"
                        placeholder="Last name"
                        {...register('lastName')}
                      />
                      <FieldError>{errors.lastName?.message}</FieldError>
                    </div>
                    <PhoneField
                      id="contactPhoneNumber"
                      label="Phone#"
                      registerProps={register('contactPhoneNumber')}
                      error={errors.contactPhoneNumber?.message}
                    />
                    <PhoneField
                      id="vitelPhoneNumber"
                      label="Vitel Wireless Primary Phone#"
                      registerProps={register('vitelPhoneNumber')}
                      error={errors.vitelPhoneNumber?.message}
                      optional
                    />
                  </div>
                  <div className="registration-info-box registration-kyc-box">
                    <strong>Connect a KYC profile</strong>
                    <span>
                      A Vitel Wireless number links your company account to a KYC
                      profile. You can add it later from your profile after opening
                      the account.
                    </span>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="registration-step-content">
                  <div className="registration-info-box">
                    <strong>Web &amp; Mobile App login information</strong>
                    <span>
                      These credentials will be used to sign in to the OgaApp web
                      and mobile app.
                    </span>
                  </div>
                  <div className="registration-form-grid">
                    <div className="registration-field registration-field-full">
                      <FieldLabel htmlFor="userName" required>
                        Login username
                      </FieldLabel>
                      <CFormInput
                        id="userName"
                        type="text"
                        autoComplete="username"
                        placeholder="Choose a login username"
                        {...register('userName')}
                      />
                      <span className="registration-hint">
                        You will use this to log into OgaApp web &amp; mobile app.
                      </span>
                      <FieldError>{errors.userName?.message}</FieldError>
                    </div>
                    <div className="registration-field">
                      <FieldLabel htmlFor="password" required>
                        Password
                      </FieldLabel>
                      <CFormInput
                        id="password"
                        type="password"
                        autoComplete="new-password"
                        placeholder="Enter password"
                        {...register('password')}
                      />
                      <FieldError>{errors.password?.message}</FieldError>
                    </div>
                    <div className="registration-field">
                      <FieldLabel htmlFor="confirmPassword" required>
                        Confirm Password
                      </FieldLabel>
                      <CFormInput
                        id="confirmPassword"
                        type="password"
                        autoComplete="new-password"
                        placeholder="Re-enter password"
                        {...register('confirmPassword')}
                      />
                      <FieldError>{errors.confirmPassword?.message}</FieldError>
                    </div>
                  </div>
                  <div className="registration-password-note">
                    <span className="registration-password-check">✓</span>
                    Minimum of 8 characters, including 1 uppercase letter and 1
                    number.
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="registration-step-content">
                  <div className="registration-funding-hero">
                    <span className="registration-funding-icon">₦</span>
                    <div>
                      <h3>Fund your e-Wallet</h3>
                      <p>
                        OgaApp uses a prepaid e-wallet to fund services such as phone
                        numbers, Xphone, Auto-Attendant, SecureMe, and SecureDevice.
                      </p>
                    </div>
                  </div>
                  <div className="registration-info-box">
                    Enter an amount below and click Register to proceed to payment
                    and complete registration.
                  </div>
                  <div className="registration-field registration-amount-field">
                    <FieldLabel htmlFor="amount" required>
                      Enter Amount
                    </FieldLabel>
                    <div className="registration-amount-input">
                      <span>₦</span>
                      <CFormInput
                        id="amount"
                        type="number"
                        min={MINIMUM_TOP_UP}
                        step="100"
                        inputMode="numeric"
                        placeholder="10,000"
                        {...register('amount')}
                      />
                    </div>
                    <span className="registration-hint">
                      Minimum of N{MINIMUM_TOP_UP.toLocaleString()} required.
                    </span>
                    <FieldError>{errors.amount?.message}</FieldError>
                  </div>
                  <div className="registration-quick-amounts" aria-label="Quick amounts">
                    {[10000, 25000, 50000, 100000].map((amount) => (
                      <button
                        type="button"
                        key={amount}
                        className={selectedAmount === amount ? 'is-selected' : ''}
                        onClick={() => selectAmount(amount)}
                      >
                        ₦{amount.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  <label className="registration-terms" htmlFor="termsConditions">
                    <input
                      id="termsConditions"
                      type="checkbox"
                      {...register('termsConditions')}
                    />
                    <span>
                      I accept the{' '}
                       <a href="/user-authentication">
                         Terms and Conditions of services
                       </a>
                    </span>
                  </label>
                  <FieldError>{errors.termsConditions?.message}</FieldError>
                </div>
              )}

              {(showError || registerError) && (
                <div className="registration-alert" role="alert">
                  {registerError}
                </div>
              )}

              <div className="registration-actions">
                {currentStep > 0 && (
                  <button
                    type="button"
                    className="registration-secondary-button"
                    onClick={goToPreviousStep}
                  >
                    Back
                  </button>
                )}
                {currentStep < REGISTRATION_STEPS.length - 1 ? (
                  <button
                    type="button"
                    className="registration-primary-button"
                    onClick={goToNextStep}
                  >
                    Continue <span aria-hidden="true">→</span>
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="registration-primary-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner size="sm" /> Starting payment…
                      </>
                    ) : (
                      'Register'
                    )}
                  </button>
                )}
              </div>
            </CForm>
          </section>

          <aside className="registration-summary">
            <h2>Before you register</h2>
            <ul>
              <li>Have your company and office details ready.</li>
              <li>Use a valid phone number and email address.</li>
              <li>Choose a login username and secure password.</li>
              <li>Fund your e-Wallet with at least N10,000.</li>
            </ul>
            <div className="registration-summary-total">
              <span>Minimum funding</span>
              <strong>₦{MINIMUM_TOP_UP.toLocaleString()}</strong>
            </div>
            <p>
              By continuing, you can add optional services such as Xphone,
              Auto-Attendant, OgaBoiz, SecureDevice, and SecureMe after your
              account is active.
            </p>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default OGAAppRegister;
